import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  TextField,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import {
  validateDiscountCode,
  DiscountCodeValidation,
} from '@/api/subscriptionsAPI'
import { useAuth0 } from '@/utils/auth/auth0'

type FrequencyMultiplier = 1 | 3 | 12

interface DiscountCodeInputProps {
  originalPrice: number
  frequencyMultiplier: FrequencyMultiplier
  onValidationChange: (validation: DiscountCodeValidation | null) => void
  initialCode?: string
  label?: string
  placeholder?: string
  disabled?: boolean
}

export default function DiscountCodeInput({
  originalPrice,
  frequencyMultiplier,
  onValidationChange,
  initialCode = '',
  label = 'Discount Code',
  placeholder = 'Enter promotional code (optional)',
  disabled = false,
}: DiscountCodeInputProps) {
  const { getAccessTokenSilently } = useAuth0()
  const [code, setCode] = useState<string>(initialCode)
  const [validation, setValidation] = useState<DiscountCodeValidation | null>(
    null,
  )
  const [isValidating, setIsValidating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const debounceTimer = useRef<NodeJS.Timeout | null>(null)

  const runValidation = useCallback(
    async (input: string) => {
      if (!input || input.trim().length === 0) {
        setValidation(null)
        setError(null)
        onValidationChange(null)
        return
      }

      setIsValidating(true)
      setError(null)

      try {
        const token = await getAccessTokenSilently()
        if (!token) {
          throw new Error('No authentication token')
        }

        const result = await validateDiscountCode(
          input.trim(),
          originalPrice,
          frequencyMultiplier,
          token,
        )

        if (result.valid) {
          setValidation(result)
          setError(null)
          onValidationChange(result)
        } else {
          setValidation(null)
          setError(result.error_message || 'Invalid discount code')
          onValidationChange(null)
        }
      } catch {
        setValidation(null)
        setError('Unable to validate discount code')
        onValidationChange(null)
      } finally {
        setIsValidating(false)
      }
    },
    [
      getAccessTokenSilently,
      onValidationChange,
      originalPrice,
      frequencyMultiplier,
    ],
  )

  const debouncedValidation = useCallback(
    (input: string) => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
      debounceTimer.current = setTimeout(() => {
        runValidation(input)
      }, 500)
    },
    [runValidation],
  )

  // Re-validate when the upstream price/frequency changes while a code is
  // entered. Intentionally NOT depending on `code` — typing is handled by the
  // debounced path in handleChange, so we don't want each keystroke to
  // re-trigger an immediate validation here.
  useEffect(() => {
    if (code) {
      runValidation(code)
    }
  }, [originalPrice, frequencyMultiplier])

  const handleChange = (value: string) => {
    setCode(value)
    debouncedValidation(value)
  }

  return (
    <Box>
      <TextField
        fullWidth
        label={label}
        value={code}
        onChange={(e) => handleChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        error={!!error}
        helperText={error || undefined}
        sx={{
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#757575',
          },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {isValidating && <CircularProgress size={20} />}
              {validation?.valid && !isValidating && (
                <CheckCircleIcon color="success" />
              )}
            </InputAdornment>
          ),
        }}
      />
      {validation?.valid && (
        <Chip
          icon={<CheckCircleIcon />}
          label={`Discount: ${validation.discount_value}% off${
            validation.duration_months
              ? ` for ${validation.duration_months} ${
                  validation.duration_months === 1 ? 'month' : 'months'
                }`
              : ''
          }`}
          color="success"
          sx={{ mt: 1 }}
        />
      )}
    </Box>
  )
}
